import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Stack, Select, MenuItem,
  FormControl, Button, Divider, Alert, IconButton, Tooltip,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, LinearProgress, Checkbox, FormControlLabel,
  Radio, RadioGroup,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InboxIcon from '@mui/icons-material/Inbox';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SUPPLIERS, PRICE_BOOKS, PRODUCTS } from '../data/mockData';
import { BRAND } from '../theme/tokens';
import { CollapseDivider, COLLAPSE_THUMB_WIDTH } from '../components/CollapseDivider';
import { DocumentPreviewRail } from '../components/DocumentPreviewRail';
import { StatusChip } from '../components/StatusChip';
import { CodeCell } from '../components/CodeCell';
import { ResolveCodeDialog } from '../components/ResolveCodeDialog';
import { useData } from '../context/DataContext';

const FI_ORANGE = '#F57C00';
const FI_BLUE = '#027BFF';

function cheapest(code: string): string {
  let bestId = 'sup2'; let bestPrice = 9999;
  for (const s of SUPPLIERS) {
    const p = PRICE_BOOKS[s.id]?.[code];
    if (p !== undefined && p < bestPrice) { bestPrice = p; bestId = s.id; }
  }
  return bestId;
}

interface POLine {
  id: number;
  inventoryCode: string;
  // v1.17 — supplier's own code, kept distinct from inventoryCode. Set when a
  // line is imported from a parsed quote so it can be reconciled to an internal
  // code without conflation.
  supplierCode?: string;
  description: string;
  qty: number;
  supplierId: string;
  receivedQty: number;
}

interface ParsedQuoteLine {
  id: number;
  supplierCode: string;
  description: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  gstTreatment: 'ex' | 'inc';
  leadTimeDays: number | null;
}

interface ParsedQuote {
  supplierName: string;
  supplierAbn?: string;
  quoteNumber?: string;
  quoteDate?: string;
  validUntil?: string;
  customerRef?: string;
  deliveryTerms?: string;
  paymentTerms?: string;
  documentGst: 'ex' | 'inc';
  subtotalExGst: number;
  totalGst: number;
  totalIncGst: number;
  notes?: string;
  lines: ParsedQuoteLine[];
}

const DEMO_QUOTE: ParsedQuote = {
  supplierName: 'Kingy Steel Supplies P/L',
  supplierAbn: '12 345 678 901',
  quoteNumber: 'Q-2026-4418',
  quoteDate: '2026-03-20',
  validUntil: '2026-04-20',
  customerRef: 'Kingy March Service Run',
  deliveryTerms: 'Delivered to Kingy Workshop, Yandina',
  paymentTerms: '30 days EOM',
  documentGst: 'ex',
  subtotalExGst: 1280.00,
  totalGst: 128.00,
  totalIncGst: 1408.00,
  notes: 'Prices hold for 30 days. Stock subject to availability.',
  lines: [
    { id: 1, supplierCode: 'STL-RHS-50',     description: 'RHS 50x50x3mm Mild Steel — 6m length',   qty: 12, unitPrice: 42.50, lineTotal: 510.00, gstTreatment: 'ex', leadTimeDays: 2 },
    { id: 2, supplierCode: 'STL-FB-25',      description: 'Flat Bar 25x5mm Mild Steel — 6m length', qty: 8,  unitPrice: 18.75, lineTotal: 150.00, gstTreatment: 'ex', leadTimeDays: 2 },
    { id: 3, supplierCode: 'STL-RBAR-12',    description: 'Reinforcing Bar N12 — 6m length',        qty: 20, unitPrice: 14.20, lineTotal: 284.00, gstTreatment: 'ex', leadTimeDays: 1 },
    { id: 4, supplierCode: 'STL-PLT-6',      description: 'Mild Steel Plate 6mm — 1200x1200',       qty: 2,  unitPrice: 168.00, lineTotal: 336.00, gstTreatment: 'ex', leadTimeDays: 5 },
  ],
};

interface BillRow {
  id: string;
  number: string;
  status: 'Draft' | 'Approved' | 'Paid';
  date: string;
  supplierInvoice: string;
  totalInc: number;
}

// Line items that come from BILL-004 when backfilled
const BACKFILL_LINES: POLine[] = [
  { id: 1, inventoryCode: '026611',    description: 'Hydraulic Filter — Heavy Duty', qty: 1, supplierId: 'sup4', receivedQty: 1 },
  { id: 2, inventoryCode: '007303AA',  description: 'Circlip — Internal 35mm',        qty: 3, supplierId: 'sup4', receivedQty: 3 },
  { id: 3, inventoryCode: 'SEAL-HYD',  description: 'Seal Kit — Hydraulic Cylinder',  qty: 1, supplierId: 'sup4', receivedQty: 1 },
];

export const POPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, addProduct, addSupplierCodeMap, lookupSupplierCode,
    addSupplierPriceBookEntries, setSupplierPriceBookInternalCode } = useData();

  // 0-item PO — starts genuinely empty
  const [lines, setLines] = useState<POLine[]>([]);

  // Unallocated-code resolution (v1.17) — which line id is being resolved.
  const [resolveLineId, setResolveLineId] = useState<number | null>(null);
  const [poSupplier, setPoSupplier] = useState('sup4');
  const [optimised, setOptimised] = useState(false);
  const [bills, setBills] = useState<BillRow[]>([]);
  const [blankBillRaised, setBlankBillRaised] = useState(false);
  const [backfilled, setBackfilled] = useState(false);

  // ── Import Supplier Quote state (was "AI-Scan Quote") ─────────────────────
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteFile, setQuoteFile] = useState<File | null>(null);
  const [quoteFileUrl, setQuoteFileUrl] = useState<string | null>(null);
  const [quoteParsing, setQuoteParsing] = useState(false);
  const [quoteStep, setQuoteStep] = useState(0);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteAiFailure, setQuoteAiFailure] = useState<null | {
    code: 'INSUFFICIENT_CREDITS' | 'RATE_LIMITED' | 'OVERLOADED' | 'BAD_API_KEY' | 'UNKNOWN';
    message: string;
    docsUrl?: string;
  }>(null);
  const [quoteDemoMode, setQuoteDemoMode] = useState(false);
  const [quoteResult, setQuoteResult] = useState<ParsedQuote | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── AI-import → supplier price book choice (v1.18) ─────────────────────────
  // By default, importing a quote adds each supplier code to the supplier's
  // price book (per supplier). The user can opt out, or upgrade the lines from
  // non-stocked supplier items to stocked inventory items (which also creates
  // catalog Products).
  const [addToPriceBook, setAddToPriceBook] = useState(true);
  const [priceBookStocked, setPriceBookStocked] = useState(false);
  const [quotePreviewCollapsed, setQuotePreviewCollapsed] = useState(false);
  const quoteInputRef = useRef<HTMLInputElement>(null);

  const QUOTE_STEPS = [
    'Uploading quote document…',
    'Alfred is reading the page…',
    'Extracting supplier + line items…',
    'Mapping to inventory codes…',
    'Done',
  ];

  const resetQuoteDialog = () => {
    setQuoteFile(null); setQuoteFileUrl(null); setQuoteParsing(false);
    setQuoteStep(0); setQuoteError(null); setQuoteAiFailure(null); setQuoteDemoMode(false); setQuoteResult(null);
    setQuotePreviewCollapsed(false);
  };
  const closeQuoteDialog = () => {
    setQuoteDialogOpen(false);
    resetQuoteDialog();
    // Drop the deep-link query param so refresh doesn't re-open the dialog.
    if (searchParams.has('quote')) {
      searchParams.delete('quote');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Deep-link: arriving with `?quote=PO-####` (e.g. from POPrintPage's "Quote"
  // button) auto-opens the AI-Scan dialog so the user lands one click closer
  // to the action. The PO-id is informational for now (UI badge).
  useEffect(() => {
    if (searchParams.get('quote')) {
      setQuoteDialogOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('quote')]);

  const processQuoteFile = useCallback((file: File) => {
    setQuoteFile(file);
    if (file.type === 'application/pdf' || file.type.startsWith('image/')) {
      setQuoteFileUrl(URL.createObjectURL(file));
    } else {
      setQuoteFileUrl(null);
    }
    setQuoteParsing(true); setQuoteStep(0); setQuoteError(null); setQuoteAiFailure(null); setQuoteDemoMode(false);
    let stepIdx = 0;
    const stepInterval = setInterval(() => { stepIdx = Math.min(stepIdx + 1, QUOTE_STEPS.length - 2); setQuoteStep(stepIdx); }, 800);
    const formData = new FormData(); formData.append('file', file);
    fetch('/api/parse-quote', { method: 'POST', body: formData })
      .then(async r => {
        clearInterval(stepInterval);
        if (!r.ok) {
          const errBody = await r.json().catch(() => ({ error: 'Failed to parse quote' }));
          // Structured failure (credits/billing/rate-limit/etc.) → fall back to demo and capture it
          if (errBody.code) {
            setQuoteAiFailure({ code: errBody.code, message: errBody.friendly ?? errBody.error, docsUrl: errBody.docsUrl });
            setQuoteDemoMode(true);
            setQuoteStep(QUOTE_STEPS.length - 1);
            setQuoteResult(DEMO_QUOTE);
            setQuoteParsing(false);
            return;
          }
          if (r.status === 503) {
            setQuoteDemoMode(true);
            setQuoteStep(QUOTE_STEPS.length - 1);
            setQuoteResult(DEMO_QUOTE);
            setQuoteParsing(false);
            return;
          }
          throw new Error(errBody.error || 'Parse failed');
        }
        const data = await r.json();
        setQuoteStep(QUOTE_STEPS.length - 1);
        setQuoteResult(data);
        setQuoteParsing(false);
      })
      .catch(err => {
        clearInterval(stepInterval);
        setQuoteError(err.message || 'Could not parse quote');
        setQuoteParsing(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onQuoteDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processQuoteFile(f);
  };

  const useDemoQuote = () => {
    setQuoteDemoMode(true);
    setQuoteResult(DEMO_QUOTE);
    setQuoteStep(QUOTE_STEPS.length - 1);
  };

  // Apply a parsed quote to the PO: populate supplier + line items
  const applyQuoteToPo = () => {
    if (!quoteResult) return;
    // Try to match the parsed supplierName against our SUPPLIERS list
    const matched = SUPPLIERS.find(s =>
      s.name.toLowerCase().includes(quoteResult.supplierName.toLowerCase()) ||
      quoteResult.supplierName.toLowerCase().includes(s.name.toLowerCase())
    );
    if (matched) setPoSupplier(matched.id);

    // Try to match supplierCode → inventory code. v1.17 — first consult the
    // remembered per-supplier mapping, then the catalog. Keep supplierCode
    // DISTINCT and leave inventoryCode empty when there is no real match so the
    // line surfaces the red "Enter / Select Code" pill (never conflate codes).
    const supId = matched?.id ?? poSupplier;
    const mapped: POLine[] = quoteResult.lines.map((l, i) => {
      const remembered = l.supplierCode ? lookupSupplierCode(supId, l.supplierCode) : undefined;
      const product = PRODUCTS.find(p =>
        p.code.toLowerCase() === l.supplierCode.toLowerCase() ||
        p.description?.toLowerCase().includes(l.description.toLowerCase().slice(0, 20))
      );
      return {
        id: Date.now() + i,
        inventoryCode: remembered ?? product?.code ?? '',
        supplierCode: l.supplierCode || undefined,
        description: l.description,
        qty: l.qty || 1,
        supplierId: supId,
        receivedQty: 0,
      };
    });
    setLines(prev => [...prev, ...mapped]);

    // v1.18 — by default, add each supplier code to this supplier's price book.
    if (addToPriceBook) {
      const entries = quoteResult.lines
        .filter(l => (l.supplierCode || '').trim())
        .map(l => {
          const internalCode = (l.supplierCode ? lookupSupplierCode(supId, l.supplierCode) : undefined)
            ?? PRODUCTS.find(p => p.code.toLowerCase() === l.supplierCode.toLowerCase())?.code;
          return {
            supplierId: supId,
            supplierCode: l.supplierCode,
            description: l.description,
            unitPrice: l.unitPrice,
            internalCode,
            stocked: priceBookStocked,
            source: 'quote' as const,
          };
        });
      addSupplierPriceBookEntries(entries);

      // Stocked items also become catalog products (if not already present).
      if (priceBookStocked) {
        for (const l of quoteResult.lines) {
          const sc = (l.supplierCode || '').trim();
          if (!sc) continue;
          if (PRODUCTS.some(p => p.code.toLowerCase() === sc.toLowerCase())) continue;
          const created = addProduct({
            code: sc, description: l.description, category: 'Uncategorised',
            primarySupplier: supId, purchasePrice: l.unitPrice,
            markup: matched ? 0 : 45, stock: 0, minStock: 0,
          });
          addSupplierCodeMap({ supplierId: supId, supplierCode: sc, internalCode: created.code });
          setSupplierPriceBookInternalCode(supId, sc, created.code);
        }
      }
    }
    closeQuoteDialog();
  };

  const updateLine = (id: number, supplierId: string) =>
    setLines(ls => ls.map(l => l.id === id ? { ...l, supplierId } : l));

  // ── Unallocated-code resolution (v1.17) ────────────────────────────────────
  const handleResolveLine = (lineId: number, internalCode: string, remember: boolean) => {
    const target = lines.find(l => l.id === lineId);
    setLines(ls => ls.map(l => l.id === lineId ? { ...l, inventoryCode: internalCode } : l));
    if (remember && target?.supplierCode) {
      addSupplierCodeMap({ supplierId: target.supplierId, supplierCode: target.supplierCode, internalCode });
    }
    // v1.18 — link the supplier price-book entry to the internal code (upsert
    // so the code lands in the supplier's price book even if it wasn't there).
    if (target?.supplierCode) {
      const prod = products.find(p => p.code === internalCode);
      addSupplierPriceBookEntries([{
        supplierId: target.supplierId, supplierCode: target.supplierCode,
        description: target.description, unitPrice: prod?.purchasePrice ?? 0,
        internalCode, stocked: false, source: 'quote',
      }]);
    }
    setResolveLineId(null);
  };

  const handleCreateProductPO = (draft: { code: string; description: string; category: string; purchasePrice: number; markup: number; minStock: number }) => addProduct({
    code: draft.code,
    description: draft.description,
    category: draft.category,
    primarySupplier: poSupplier,
    purchasePrice: draft.purchasePrice,
    markup: draft.markup,
    stock: 0,
    minStock: draft.minStock,
  });

  const handleGenerateBlankBill = () => {
    const newBill: BillRow = {
      id: 'BILL-004',
      number: 'BILL-2026-005',
      status: 'Draft',
      date: new Date().toISOString().split('T')[0],
      supplierInvoice: '',
      totalInc: 0,
    };
    setBills([newBill]);
    setBlankBillRaised(true);
  };

  // Simulate backfill — called after "Add line items to PO" in the bill dialog
  const handleBackfillFromBill = () => {
    setLines(BACKFILL_LINES);
    setBackfilled(true);
    setBills(prev => prev.map(b => ({
      ...b,
      supplierInvoice: '247-88213',
      totalInc: 80.00,
      status: 'Approved' as const,
    })));
  };

  const total = lines.reduce((s, l) => {
    const price = PRICE_BOOKS[l.supplierId]?.[l.inventoryCode] ?? PRODUCTS.find(p => p.code === l.inventoryCode)?.purchasePrice ?? 0;
    return s + price * l.qty;
  }, 0);
  const totalGst = total * 0.1;
  const billsTotal = bills.reduce((s, b) => s + b.totalInc, 0);

  const allReceived = lines.length > 0 && lines.every(l => l.receivedQty >= l.qty);

  return (
    <Box>
      {optimised && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOptimised(false)}>
          Lines optimised to cheapest available supplier per item.
        </Alert>
      )}

      {backfilled && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
          <strong>{lines.length} line items backfilled</strong> from BILL-2026-005 · All quantities marked as Received via supplier invoice.
        </Alert>
      )}

      {/* ── PO Header ──────────────────────────────────────────────── */}
      <Paper sx={{ p: 2.5, mb: 2.5, border: '1px solid #e0e0e0' }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <Typography variant="h6" fontWeight={800} sx={{ color: FI_ORANGE }}>PO-4575</Typography>
          <Chip
            label={backfilled ? `${lines.length} items` : '0 items'}
            size="small"
            sx={{
              backgroundColor: backfilled ? '#e8f5e9' : '#fff3e0',
              color: backfilled ? '#2e7d32' : FI_ORANGE,
              fontWeight: 700, fontSize: '0.68rem', height: 20,
            }}
          />
          {backfilled && allReceived && (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
              label="All Received"
              size="small"
              sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.68rem', height: 20 }}
            />
          )}
          <Box flex={1} />
          <Stack direction="row" spacing={1} alignItems="center">
            <LinkIcon sx={{ fontSize: 14, color: FI_BLUE }} />
            <Typography variant="caption" sx={{ color: FI_BLUE, fontWeight: 600 }}>JOB #234</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Created by Daniel Cant, 21 Mar 2026 09:14 AM
          </Typography>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3, mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.5 }}>Supplier</Typography>
            <FormControl size="small" fullWidth>
              <Select value={poSupplier} onChange={e => setPoSupplier(e.target.value)}>
                {SUPPLIERS.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
            {SUPPLIERS.find(s => s.id === poSupplier) && (
              <Box mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {SUPPLIERS.find(s => s.id === poSupplier)?.terms} &nbsp;·&nbsp;
                  {SUPPLIERS.find(s => s.id === poSupplier)?.email}
                </Typography>
              </Box>
            )}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.5 }}>Site</Typography>
            <Typography variant="body2" fontWeight={600}>Kingy's Workshop</Typography>
            <Typography variant="caption" color="text.secondary">Shed 6/1505 Nambour Connection Rd, Yandina QLD 4561</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.5 }}>Project</Typography>
            <Typography variant="body2" fontWeight={600}>Kingy's Diesel — March Service Run</Typography>
            <Typography variant="caption" color="text.secondary">PROJ-7 · Start 01/03/2026 · End 31/03/2026</Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2, mb: 2 }}>
          {[
            { label: 'Title',          value: 'Bunnings — account purchase' },
            { label: 'Status',         value: backfilled ? 'Received' : 'Draft' },
            { label: 'Invoice term',   value: '30 Days EOM' },
            { label: 'Delivery status', value: backfilled ? 'Delivered' : '—' },
            { label: 'Delivery due',   value: '—' },
            { label: 'PO Date',        value: '21/03/2026' },
          ].map(({ label, value }) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary" display="block"
                sx={{ fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', mb: 0.3 }}>{label}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem', color: label === 'Status' && backfilled ? '#2e7d32' : 'inherit' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary" display="block"
            sx={{ fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', mb: 0.5 }}>Internal notes</Typography>
          <TextField multiline rows={2} fullWidth size="small"
            placeholder="Add internal notes…"
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }} />
        </Box>
      </Paper>

      {/* ── Jobs & Inventory ────────────────────────────────────────── */}
      <Paper sx={{ border: '1px solid #e0e0e0', mb: 2, overflow: 'hidden' }}>
        <Box sx={{ backgroundColor: '#f5f0ff', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" fontWeight={700} color={BRAND}>
            Jobs &amp; Inventory
            {lines.length > 0 && (
              <Typography component="span" variant="caption" sx={{ ml: 1, color: '#888', fontWeight: 400 }}>
                · {lines.length} line{lines.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {lines.length > 0 && !backfilled && (
              <Button size="small" startIcon={<AutoFixHighIcon />}
                onClick={() => { setLines(ls => ls.map(l => ({ ...l, supplierId: cheapest(l.inventoryCode) }))); setOptimised(true); setTimeout(() => setOptimised(false), 3000); }}
                sx={{ textTransform: 'none', fontSize: '0.68rem', color: '#2e7d32', py: 0 }}>
                Auto-optimise
              </Button>
            )}
            <Tooltip arrow placement="top" title={
              <Box sx={{ fontSize: '0.68rem', lineHeight: 1.5, maxWidth: 260 }}>
                <Box sx={{ fontWeight: 700, mb: 0.3 }}>Import Supplier Quote</Box>
                <Box>Drop a PDF or image of a supplier quote. Alfred extracts the supplier, line items, prices, lead times, and validity — then populates the PO lines for you.</Box>
                <Box sx={{ mt: 0.5, opacity: 0.85 }}>Example: a Kingy Steel quote PDF → 4 line items pre-filled, supplier auto-selected, ready to send.</Box>
              </Box>
            }>
              <Button size="small" variant="contained" startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                onClick={() => setQuoteDialogOpen(true)}
                sx={{ textTransform: 'none', fontSize: '0.68rem', fontWeight: 700,
                  backgroundColor: BRAND, '&:hover': { backgroundColor: '#5a3e98' }, py: 0.3, px: 1.2 }}>
                Import Supplier Quote
              </Button>
            </Tooltip>
          </Stack>
        </Box>

        {lines.length === 0 ? (
          /* ── Empty state ── */
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <InboxIcon sx={{ fontSize: 44, color: '#e0e0e0', mb: 1 }} />
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#aaa', mb: 0.5 }}>
              No line items yet
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#bbb', mb: 2.5, maxWidth: 340, mx: 'auto', lineHeight: 1.5 }}>
              This is a 0-item placeholder PO. Generate a Blank Bill below, upload the supplier invoice PDF,
              then backfill the line items back here.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                size="small"
                startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                onClick={() => setQuoteDialogOpen(true)}
                sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                  '&:hover': { backgroundColor: '#5a3e98' } }}
              >
                Import Supplier Quote
              </Button>
              {blankBillRaised && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleBackfillFromBill}
                  sx={{ color: BRAND, borderColor: BRAND, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}
                >
                  Simulate: Backfill from BILL-2026-005
                </Button>
              )}
            </Stack>
            <Typography sx={{ fontSize: '0.68rem', color: '#bbb', mt: 1.5 }}>
              Upload a supplier quote PDF/image — Alfred extracts the lines
            </Typography>
          </Box>
        ) : (
          /* ── Line items table ── */
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                {['Code', 'Description', 'Qty', 'Received Qty', 'Supplier', 'Purchase $', 'Markup %', 'Sales $', 'Tax', 'Amount', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#555', whiteSpace: 'nowrap', py: 1 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(line => {
                const product = PRODUCTS.find(p => p.code === line.inventoryCode);
                const purchase = PRICE_BOOKS[line.supplierId]?.[line.inventoryCode] ?? product?.purchasePrice ?? 0;
                const markup = product?.markup ?? 65;
                const salesP = purchase * (1 + markup / 100);
                const best = cheapest(line.inventoryCode);
                const isBest = best === line.supplierId;
                const fullyReceived = line.receivedQty >= line.qty;
                return (
                  <TableRow key={line.id} hover sx={{ backgroundColor: fullyReceived ? '#f1f8e9' : 'inherit' }}>
                    <TableCell>
                      <CodeCell
                        internalCode={line.inventoryCode}
                        supplierCode={line.supplierCode}
                        onResolve={() => setResolveLineId(line.id)}
                        allowReresolve
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{line.description}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', textAlign: 'center' }}>{line.qty}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <Typography sx={{
                          fontSize: '0.78rem', fontWeight: 700,
                          color: fullyReceived ? '#2e7d32' : line.receivedQty > 0 ? FI_ORANGE : '#bbb',
                        }}>
                          {line.receivedQty} / {line.qty}
                        </Typography>
                        {fullyReceived && <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <FormControl size="small">
                        <Select value={line.supplierId} onChange={e => updateLine(line.id, e.target.value)}
                          variant="standard" disableUnderline
                          renderValue={v => (
                            <Chip label={SUPPLIERS.find(s => s.id === v)?.code} size="small"
                              sx={{ backgroundColor: isBest ? '#2e7d32' : BRAND, color: '#fff', fontWeight: 700, fontSize: '0.65rem' }} />
                          )}>
                          {SUPPLIERS.map(s => {
                            const p = PRICE_BOOKS[s.id]?.[line.inventoryCode];
                            return (
                              <MenuItem key={s.id} value={s.id}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <span>{s.name}</span>
                                  {p !== undefined && <Typography variant="caption" color="text.secondary">${p.toFixed(2)}</Typography>}
                                </Stack>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{purchase > 0 ? purchase.toFixed(2) : '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{markup}.00</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{purchase > 0 ? salesP.toFixed(2) : '—'}</TableCell>
                    <TableCell><Chip label="YES" size="small" color="success" sx={{ fontSize: '0.65rem' }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{purchase > 0 ? purchase.toFixed(2) : '—'}</TableCell>
                    <TableCell><IconButton size="small"><DeleteIcon sx={{ fontSize: 14, color: '#ccc' }} /></IconButton></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Box sx={{ p: 1.5, borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button size="small" startIcon={<AddIcon />} sx={{ color: FI_ORANGE, textTransform: 'none' }}>+ Item</Button>
          {lines.length > 0 && (
            <Button size="small" sx={{ color: '#888', textTransform: 'none', fontSize: '0.72rem' }}>Import CSV</Button>
          )}
        </Box>
      </Paper>

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Paper sx={{ p: 2, minWidth: 260, border: '1px solid #e0e0e0' }}>
          {[
            ['Total Services (Ex)', '$0.00'],
            ['Total Products (Ex)', `$${total.toFixed(2)}`],
            ['Subtotal', `$${total.toFixed(2)}`],
            ['Tax amount', `$${totalGst.toFixed(2)}`],
          ].map(([l, v]) => (
            <Stack key={l} direction="row" justifyContent="space-between" mb={0.5}>
              <Typography variant="caption" color="text.secondary">{l}</Typography>
              <Typography variant="caption">{v}</Typography>
            </Stack>
          ))}
          <Divider sx={{ my: 0.5 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={800}>TOTAL</Typography>
            <Typography variant="body2" fontWeight={800}>${(total + totalGst).toFixed(2)}</Typography>
          </Stack>
        </Paper>
      </Box>

      {/* Action buttons */}
      <Stack direction="row" spacing={1} mb={3} flexWrap="wrap">
        {['Close', 'Preview', 'Download', 'Email', 'Copy', 'Create job', 'Submit', 'Receive all'].map(b => (
          <Button key={b} variant="contained" size="small"
            sx={{
              backgroundColor: ['Submit', 'Receive all'].includes(b) ? FI_ORANGE : BRAND,
              textTransform: 'none', fontSize: '0.78rem',
            }}>
            {b}
          </Button>
        ))}
        <Button variant="contained" size="small"
          sx={{ backgroundColor: '#c62828', textTransform: 'none', fontSize: '0.78rem' }}>Delete</Button>
      </Stack>

      {/* ── Bills section ───────────────────────────────────────────── */}
      <Paper sx={{ border: '1px solid #e0e0e0', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon sx={{ fontSize: 18, color: BRAND }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#333' }}>Bills</Typography>
          {bills.length > 0 && (
            <Chip label={bills.length} size="small"
              sx={{ fontSize: '0.65rem', height: 18, backgroundColor: '#f5f0ff', color: BRAND, fontWeight: 700, ml: 0.5 }} />
          )}
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND, py: 1 }}>Number</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND }}>Supplier invoice</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND, textAlign: 'right' }}>Total (inc)</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {bills.map(bill => {
              return (
                <TableRow key={bill.id} hover sx={{ cursor: 'pointer', backgroundColor: '#fffde7' }}
                  onClick={() => navigate(`/bills/${bill.id}`)}>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, color: BRAND }}>{bill.number}</TableCell>
                  <TableCell>
                    <StatusChip status={bill.status} dense />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#555' }}>{bill.date}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: bill.supplierInvoice ? '#333' : '#aaa', fontStyle: bill.supplierInvoice ? 'normal' : 'italic' }}>
                    {bill.supplierInvoice || 'not yet uploaded'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right' }}>
                    {bill.totalInc.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {!bill.supplierInvoice && (
                      <Tooltip title="Upload supplier invoice PDF">
                        <Button size="small" variant="contained" startIcon={<UploadFileIcon sx={{ fontSize: 12 }} />}
                          sx={{ textTransform: 'none', fontSize: '0.65rem', backgroundColor: FI_ORANGE }}
                          onClick={e => { e.stopPropagation(); navigate(`/bills/${bill.id}`); }}>
                          Upload PDF
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            <TableRow sx={{ backgroundColor: '#fafafa', borderTop: bills.length ? '2px solid #e0e0e0' : undefined }}>
              <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: '0.75rem', py: 1 }}>Totals</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textAlign: 'right' }}>{billsTotal.toFixed(2)}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell colSpan={4} sx={{ fontSize: '0.75rem', color: '#888', pb: 1 }}>Outstandi…</TableCell>
              <TableCell sx={{ fontSize: '0.78rem', textAlign: 'right', color: '#888', pb: 1 }}>{billsTotal.toFixed(2)}</TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        {bills.length === 0 && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#888' }}>This list is empty.</Typography>
          </Box>
        )}

        {blankBillRaised && !backfilled && (
          <Alert severity="success" sx={{ mx: 2, my: 1.5, fontSize: '0.78rem' }}
            action={
              <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                sx={{ textTransform: 'none', fontSize: '0.72rem', color: BRAND, fontWeight: 700 }}
                onClick={() => navigate('/bills/BILL-004')}>
                Open bill &amp; upload PDF
              </Button>
            }>
            <strong>BILL-2026-005</strong> created and linked to PO-4575 · JOB #234
          </Alert>
        )}

        <Box sx={{ px: 2, py: 2, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" size="small" startIcon={<SearchIcon sx={{ fontSize: 14 }} />}
            sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
            + Find Bill
          </Button>
          <Button variant="contained" size="small" startIcon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
            sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
            + Generate Bill from PO
          </Button>
          <Button variant="contained" size="small" startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
            disabled={blankBillRaised}
            onClick={handleGenerateBlankBill}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
              backgroundColor: blankBillRaised ? undefined : BRAND,
              '&.Mui-disabled': { backgroundColor: '#e0d5f5', color: BRAND },
            }}>
            {blankBillRaised ? '✓ Blank Bill Raised' : '+ Generate Blank Bill'}
          </Button>
        </Box>
      </Paper>

      {/* ── Credit Notes ────────────────────────────────────────────── */}
      <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#333' }}>Credit Notes</Typography>
        </Box>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: '0.82rem', color: '#888' }}>This list is empty.</Typography>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <Button variant="contained" size="small"
            sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
            + Generate Credit Note
          </Button>
        </Box>
      </Paper>

      {/* ── Import Supplier Quote dialog (was "AI-Scan Quote") ────────────── */}
      <Dialog open={quoteDialogOpen} onClose={closeQuoteDialog} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, borderBottom: '1px solid #eee' }}>
          <AutoAwesomeIcon sx={{ color: BRAND }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#333' }}>Supplier Quote → PO</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>
              AI-scan a quote to pre-fill PO lines · supplier auto-selected · prices, qty, lead times extracted
            </Typography>
          </Box>
          {quoteDemoMode && (
            <Chip label="DEMO DATA" size="small"
              sx={{ backgroundColor: '#fff3e0', color: FI_ORANGE, fontWeight: 700, fontSize: '0.62rem', height: 20 }} />
          )}
          <IconButton size="small" onClick={closeQuoteDialog}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>

          {/* Stage 1 — Upload */}
          {!quoteFile && !quoteResult && (
            <Box>
              <Box
                onClick={() => quoteInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onQuoteDrop}
                sx={{ p: 4, border: `2px dashed ${isDragging ? BRAND : '#ddd'}`,
                  borderRadius: 2, textAlign: 'center', cursor: 'pointer',
                  backgroundColor: isDragging ? '#f5f0ff' : '#fafafa', transition: 'all 0.2s',
                  '&:hover': { borderColor: BRAND, backgroundColor: '#f5f0ff' } }}>
                <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? BRAND : '#ccc', mb: 1 }} />
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#555', mb: 0.5 }}>
                  Drop supplier quote here
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#aaa', mb: 2 }}>
                  PDF or image (JPG, PNG) — up to 25 MB
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Chip icon={<PictureAsPdfIcon sx={{ fontSize: 12 }} />} label="PDF" size="small" />
                  <Chip label="JPG / PNG" size="small" />
                </Stack>
                <input ref={quoteInputRef} type="file" accept=".pdf,application/pdf,image/*" hidden
                  onChange={e => { const f = e.target.files?.[0]; if (f) processQuoteFile(f); e.target.value = ''; }} />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, p: 1.5, backgroundColor: '#f5f0ff', borderRadius: 1, border: `1px solid ${BRAND}22` }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: BRAND }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#555', flex: 1 }}>
                  <strong>What gets extracted:</strong> supplier name &amp; ABN · quote # and date · valid-until date · delivery &amp; payment terms ·
                  each line (code, description, qty, unit price, lead time) · subtotal &amp; GST.
                </Typography>
              </Box>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button size="small" onClick={useDemoQuote}
                  sx={{ textTransform: 'none', color: BRAND, fontWeight: 700, fontSize: '0.72rem' }}>
                  No quote handy? → Try with demo data
                </Button>
              </Box>
            </Box>
          )}

          {/* Stage 2 — Parsing */}
          {quoteFile && quoteParsing && (
            <Box sx={{ py: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <CircularProgress size={22} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#333' }}>
                    {QUOTE_STEPS[quoteStep]}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>{quoteFile.name} · {(quoteFile.size / 1024).toFixed(0)} KB</Typography>
                </Box>
              </Stack>
              <LinearProgress variant="determinate" value={((quoteStep + 1) / QUOTE_STEPS.length) * 100}
                sx={{ height: 5 }} />
            </Box>
          )}

          {/* Stage 3 — Friendly AI failure (credits / rate-limit / overloaded / bad key) */}
          {quoteAiFailure && (() => {
            const { code, message, docsUrl } = quoteAiFailure;
            const titleByCode: Record<typeof code, string> = {
              INSUFFICIENT_CREDITS: 'AI parsing paused — API credits exhausted',
              RATE_LIMITED:         'AI parsing rate-limited',
              OVERLOADED:           'AI service overloaded',
              BAD_API_KEY:          'AI parsing key invalid',
              UNKNOWN:              'AI parsing unavailable',
            };
            const sev: 'info' | 'warning' = code === 'INSUFFICIENT_CREDITS' ? 'info' : 'warning';
            return (
              <Alert severity={sev} sx={{ fontSize: '0.78rem' }}
                action={docsUrl ? (
                  <Button size="small" href={docsUrl} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>
                    Top up credits
                  </Button>
                ) : <Button size="small" onClick={resetQuoteDialog} sx={{ textTransform: 'none' }}>Try again</Button>}>
                <strong>{titleByCode[code]}</strong> — {message}
              </Alert>
            );
          })()}

          {/* Stage 3 — Generic / unrecoverable parse error */}
          {quoteError && !quoteAiFailure && (
            <Alert severity="error" sx={{ fontSize: '0.78rem' }}
              action={<Button size="small" onClick={resetQuoteDialog} sx={{ textTransform: 'none' }}>Try again</Button>}>
              <strong>Couldn't parse quote:</strong> {quoteError}
            </Alert>
          )}

          {/* Stage 4 — Result preview (2-column with collapsible quote preview) */}
          {quoteResult && !quoteParsing && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>

            {/* ── LEFT: original quote PDF / image preview ── */}
            <Box sx={{
                width: quotePreviewCollapsed ? COLLAPSE_THUMB_WIDTH : 320,
                flexShrink: 0, overflow: 'hidden',
                transition: 'width 0.18s ease',
              }}>
              {quotePreviewCollapsed ? (
                <DocumentPreviewRail
                  collapsed
                  fileName={quoteFile?.name ?? (quoteDemoMode ? 'Demo quote' : 'Quote')}
                  fileUrl={quoteFileUrl}
                  fileType={quoteFile?.type}
                  demo={quoteDemoMode}
                  thumbWidth={COLLAPSE_THUMB_WIDTH}
                  expandedWidth={320}
                  minHeight={460}
                  onExpand={() => setQuotePreviewCollapsed(false)}
                />
              ) : (
              <Paper variant="outlined" sx={{ p: 0, height: 460, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 1.2, py: 0.6, borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 0.6, backgroundColor: '#fafafa' }}>
                  <PictureAsPdfIcon sx={{ fontSize: 14, color: FI_ORANGE }} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#444', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {quoteFile?.name ?? (quoteDemoMode ? 'Demo quote' : 'Quote preview')}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {quoteFileUrl && quoteFile?.type === 'application/pdf' ? (
                    <iframe src={quoteFileUrl} title="Quote preview" style={{ width: '100%', height: '100%', border: 'none' }} />
                  ) : quoteFileUrl && quoteFile?.type.startsWith('image/') ? (
                    <img src={quoteFileUrl} alt="Quote" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  ) : (
                    <Stack alignItems="center" spacing={1} sx={{ color: '#aaa' }}>
                      <PictureAsPdfIcon sx={{ fontSize: 40 }} />
                      <Typography sx={{ fontSize: '0.72rem' }}>{quoteDemoMode ? 'Demo mode — no PDF uploaded' : 'No preview available'}</Typography>
                    </Stack>
                  )}
                </Box>
              </Paper>
              )}
            </Box>

            {/* ── Lean expand/collapse divider ── */}
            <CollapseDivider
              collapsed={quotePreviewCollapsed}
              onToggle={() => setQuotePreviewCollapsed(p => !p)}
              expandLabel="Expand quote preview"
              collapseLabel="Collapse quote preview"
              topOffset={32}
            />

            {/* ── RIGHT: parsed details ── */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Header summary */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#fafaff', borderColor: `${BRAND}44` }}>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Supplier</Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: BRAND }}>{quoteResult.supplierName || '—'}</Typography>
                    {quoteResult.supplierAbn && <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>ABN {quoteResult.supplierAbn}</Typography>}
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Quote #</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{quoteResult.quoteNumber || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Quote Date</Typography>
                    <Typography sx={{ fontSize: '0.82rem' }}>{quoteResult.quoteDate || '—'}</Typography>
                  </Box>
                  {quoteResult.validUntil && (
                    <Box>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Valid Until</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: FI_ORANGE }}>{quoteResult.validUntil}</Typography>
                    </Box>
                  )}
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#888', textTransform: 'uppercase' }}>Total Inc GST</Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: BRAND }}>${quoteResult.totalIncGst.toFixed(2)}</Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#888' }}>
                      Ex ${quoteResult.subtotalExGst.toFixed(2)} + GST ${quoteResult.totalGst.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
                {(quoteResult.deliveryTerms || quoteResult.paymentTerms) && (
                  <Stack direction="row" spacing={2} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #ddd' }}>
                    {quoteResult.deliveryTerms && (
                      <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>
                        <strong>Delivery:</strong> {quoteResult.deliveryTerms}
                      </Typography>
                    )}
                    {quoteResult.paymentTerms && (
                      <Typography sx={{ fontSize: '0.7rem', color: '#555' }}>
                        <strong>Payment:</strong> {quoteResult.paymentTerms}
                      </Typography>
                    )}
                  </Stack>
                )}
                {quoteResult.notes && (
                  <Typography sx={{ fontSize: '0.68rem', color: '#777', mt: 1, fontStyle: 'italic' }}>
                    Note: {quoteResult.notes}
                  </Typography>
                )}
              </Paper>

              {/* Line items table */}
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#444', mb: 0.8 }}>
                {quoteResult.lines.length} line item{quoteResult.lines.length !== 1 ? 's' : ''} detected
              </Typography>
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
                      {['Code', 'Description', 'Qty', 'Unit $', 'Line Total', 'Lead (days)'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.62rem', color: '#666', textTransform: 'uppercase', py: 0.8 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {quoteResult.lines.map(l => (
                      <TableRow key={l.id} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700, color: FI_ORANGE }}>
                          {l.supplierCode}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{l.description}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{l.qty}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', textAlign: 'right' }}>${l.unitPrice.toFixed(2)}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right' }}>${l.lineTotal.toFixed(2)}</TableCell>
                        <TableCell sx={{ fontSize: '0.72rem', color: '#888', textAlign: 'center' }}>
                          {l.leadTimeDays != null ? `${l.leadTimeDays} d` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* ── Supplier price book choice (v1.18) ─────────────────────── */}
              <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, backgroundColor: '#f7f4fc', borderColor: '#e4d4f5' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addToPriceBook}
                      onChange={e => setAddToPriceBook(e.target.checked)}
                      sx={{ py: 0.3, color: BRAND, '&.Mui-checked': { color: BRAND } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#333' }}>
                      Add these supplier codes to{' '}
                      <Box component="span" sx={{ color: BRAND }}>
                        {SUPPLIERS.find(s => s.id === (lines[0]?.supplierId ?? poSupplier))?.name
                          ?? quoteResult.supplierName}
                      </Box>
                      's price book
                    </Typography>
                  }
                />
                {addToPriceBook && (
                  <Box sx={{ pl: 4, pt: 0.3 }}>
                    <RadioGroup
                      value={priceBookStocked ? 'stocked' : 'non-stocked'}
                      onChange={e => setPriceBookStocked(e.target.value === 'stocked')}
                    >
                      <FormControlLabel value="non-stocked" control={<Radio size="small" sx={{ py: 0.2, color: BRAND, '&.Mui-checked': { color: BRAND } }} />}
                        label={<Typography sx={{ fontSize: '0.74rem' }}>Non-stocked supplier item <Box component="span" sx={{ color: '#999' }}>(price book only — default)</Box></Typography>} />
                      <FormControlLabel value="stocked" control={<Radio size="small" sx={{ py: 0.2, color: BRAND, '&.Mui-checked': { color: BRAND } }} />}
                        label={<Typography sx={{ fontSize: '0.74rem' }}>Stocked inventory item <Box component="span" sx={{ color: '#999' }}>(also create a catalog product)</Box></Typography>} />
                    </RadioGroup>
                    <Typography sx={{ fontSize: '0.68rem', color: '#8a7ca8', mt: 0.5 }}>
                      Codes are added to the supplier price book on import. Reconcile each to an inventory code on the PO to link them to your catalogue — then wait for the supplier bill.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid #eee', gap: 1 }}>
          {quoteResult && (
            <>
              <Button onClick={resetQuoteDialog} size="small"
                sx={{ textTransform: 'none', color: '#888', fontWeight: 600 }}>
                ← Scan another
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={closeQuoteDialog} size="small"
                sx={{ textTransform: 'none', color: '#888', fontWeight: 600 }}>
                Cancel
              </Button>
              <Button onClick={applyQuoteToPo} variant="contained" size="small"
                startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: BRAND,
                  '&:hover': { backgroundColor: '#5a3e98' } }}>
                Populate PO with {quoteResult.lines.length} line{quoteResult.lines.length !== 1 ? 's' : ''}
              </Button>
            </>
          )}
          {!quoteResult && (
            <Button onClick={closeQuoteDialog} size="small"
              sx={{ textTransform: 'none', color: '#888', fontWeight: 600 }}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Unallocated-code resolution (v1.17) */}
      {resolveLineId !== null && (() => {
        const ln = lines.find(l => l.id === resolveLineId);
        if (!ln) return null;
        const sup = SUPPLIERS.find(s => s.id === ln.supplierId);
        const unitPrice = PRICE_BOOKS[ln.supplierId]?.[ln.inventoryCode] ?? PRODUCTS.find(p => p.code === ln.inventoryCode)?.purchasePrice;
        return (
          <ResolveCodeDialog
            open
            supplierId={ln.supplierId}
            supplierName={sup?.name ?? 'Supplier'}
            line={{ supplierCode: ln.supplierCode ?? '', description: ln.description, unitPrice }}
            products={products}
            onClose={() => setResolveLineId(null)}
            onResolved={(internalCode, remember) => handleResolveLine(ln.id, internalCode, remember)}
            onCreateProduct={handleCreateProductPO}
          />
        );
      })()}
    </Box>
  );
};
